import React from 'react';
import { Container, Typography, Box, Paper, Divider, Grid, Chip } from '@mui/material';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import ChurchIcon from '@mui/icons-material/Church';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import GroupsIcon from '@mui/icons-material/Groups';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import FavoriteIcon from '@mui/icons-material/Favorite';
import VerifiedIcon from '@mui/icons-material/Verified';

const PURPLE = '#00F1A7';
const DARK = '#05080F';
const LIME = '#00F1A7';

const valores = [
  { icon: <ChurchIcon sx={{ fontSize: 40 }} />, titulo: 'Compromisso Pastoral', desc: 'Nosso software nasceu dentro da igreja, para a igreja. Entendemos a realidade eclesiástica porque a vivemos.' },
  { icon: <AutoGraphIcon sx={{ fontSize: 40 }} />, titulo: 'Inovação com Propósito', desc: 'Tecnologia não como fim, mas como meio para fortalecer comunidades e simplificar a gestão do Reino.' },
  { icon: <GroupsIcon sx={{ fontSize: 40 }} />, titulo: 'Times por Afinidade', desc: 'Reunimos profissionais que se identificam com cada projeto. Squads multidisciplinares com autonomia e foco.' },
  { icon: <EmojiObjectsIcon sx={{ fontSize: 40 }} />, titulo: 'Soluções Sob Medida', desc: 'Cada igreja é única. Nosso SaaS se adapta à sua realidade, não o contrário.' },
  { icon: <FavoriteIcon sx={{ fontSize: 40 }} />, titulo: 'Ética e Transparência', desc: 'Relações baseadas em confiança. Dados protegidos pela LGPD. Preços justos e sem letras miúdas.' },
  { icon: <VerifiedIcon sx={{ fontSize: 40 }} />, titulo: 'Suporte Real', desc: 'Gente de verdade do outro lado. Atendimento humano, rápido e comprometido com o seu sucesso.' },
];

export default function QuemSomos() {
  return (
    <>
      <PublicHeader />
      <Box sx={{ bgcolor: '#0A141C', minHeight: '70vh' }}>
        {/* Hero */}
        <Box sx={{ background: 'linear-gradient(150deg, #0A1D26, #05080F)', py: { xs: 6, md: 10 }, textAlign: 'center', color: '#E9F1EF' }}>
          <Container maxWidth="md">
            <Chip label="QUEM SOMOS" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#E9F1EF', fontWeight: 700, mb: 2 }} />
            <Typography variant="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.6rem', md: '2.5rem' }, mb: 2 }}>
              Sementes Digitais que<br />Transformam Igrejas
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '1.1rem', maxWidth: 560, mx: 'auto', lineHeight: 1.8 }}>
              Cultivando o futuro digital do Reino com tecnologia, propósito e compromisso pastoral.
            </Typography>
          </Container>
        </Box>

        {/* Sobre */}
        <Container maxWidth="md" sx={{ py: { xs: 5, md: 7 } }}>
          <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: PURPLE }}>
              Por que criamos o ConectaChurch
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 2 }}>
              Tudo começou com uma dor real. Como membros ativos de comunidades eclesiais, sentimos na
              pele o caos da gestão manual: planilhas que se perdiam, visitantes sem acompanhamento,
              frequências anotadas em papéis, líderes sobrecarregados com tarefas administrativas que
              roubavam tempo do que realmente importa — cuidar de gente.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 2 }}>
              Visitamos dezenas de igrejas — de pequenas congregações a mega templos — e ouvimos a mesma
              história: <strong>"a gente não tem ferramenta"</strong>. As soluções que existiam eram ou
              caras demais, ou complexas demais, ou simplesmente não entendiam a dinâmica de uma igreja.
              Softwares importados que tratavam membros como "clientes" e ministérios como "departamentos
              corporativos".
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
              Decidimos agir. Reunimos pastores, líderes de jovens, secretários de igreja, consolidadores
              e discipuladores. Sentamos com eles, entendemos seus fluxos reais — do primeiro contato com
              um visitante até o discipulado profundo. O resultado é o <strong>ConectaChurch</strong>: uma
              plataforma construída com teologia nos dados e tecnologia na interface. Feita por quem
              entende de igreja, para quem faz a igreja acontecer.
            </Typography>
          </Paper>

          <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: PURPLE }}>Onde tudo começa</Typography>
            <Divider sx={{ mb: 3 }} />

            <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 2 }}>
              Fundada em 2022, a <strong>SmyrnaCore — Sementes Digitais</strong> nasceu com um propósito claro:
              entregar resultados que transcendem o físico e o digital. Inspirados na perseverança da Igreja de
              Esmirna, do livro de Apocalipse, acreditamos que cada projeto é uma semente plantada para um futuro
              sólido, ético e sustentável.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9, mb: 2 }}>
              O <strong>ConectaChurch</strong> é a concretização dessa visão no universo eclesiástico. Um SaaS completo
              que nasceu dentro da igreja, para a igreja — unindo tecnologia de ponta com profundo respeito pela
              missão pastoral.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.9 }}>
              Assim como a igreja de Esmirna, acreditamos que a humildade e a perseverança são a base para um
              crescimento verdadeiro. Cada projeto que desenvolvemos é uma semente plantada com dedicação,
              esperando florescer no digital.
            </Typography>
          </Paper>

          {/* Valores */}
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: PURPLE, textAlign: 'center' }}>
            Nossos Valores
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 4 }}>
            O que nos move todos os dias
          </Typography>

          <Grid container spacing={3}>
            {valores.map((v, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <Paper sx={{ p: 3, borderRadius: 2, height: '100%', border: `1px solid ${PURPLE}15`, transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 } }}>
                  <Box sx={{ color: PURPLE, mb: 1.5 }}>{v.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, color: '#E9F1EF' }}>{v.titulo}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{v.desc}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Dados da empresa */}
          <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 3, mt: 4, bgcolor: 'rgba(0,241,167,0.10)', color: '#E9F1EF', border: '1px solid rgba(0,241,167,0.22)' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#E9F1EF' }}>
              SmyrnaCore — Sementes Digitais
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, color: '#E9F1EF' }}>
              <strong>CNPJ:</strong> 66.330.452/0001-40
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, color: '#E9F1EF' }}>
              <strong>Email:</strong> contato@smyrnacore.com.br
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, color: '#E9F1EF' }}>
              <strong>WhatsApp:</strong> (81) 99600-1383
            </Typography>
            <Typography variant="body2" sx={{ color: '#E9F1EF' }}>
              <strong>Website:</strong> smyrnacore.com.br
            </Typography>
          </Paper>
        </Container>
      </Box>
      <Footer />
    </>
  );
}
